import { ownerRouteAllowed } from 'core-access';
import { PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { CanActivateFn, Router } from '@angular/router';
import { firstValueFrom, timeout } from 'rxjs';
import { environment } from '../../../environments/environment';
import { OwnerWorkspaceService } from './owner-workspace.service';
import { OwnerPortalService } from './owner-portal.service';

export const ownerPortalGuard: CanActivateFn = async (_route, state) => {
  if (!isPlatformBrowser(inject(PLATFORM_ID))) {
    return true;
  }

  const router = inject(Router);
  const api = inject(OwnerPortalService);
  const workspace = inject(OwnerWorkspaceService);

  const session = await resolveOwnerSession(api);
  if (!session?.authenticated) {
    redirectToOwnerLogin(state.url);
    return false;
  }

  if (state.url.split('?')[0] === '/dashboard') return true;

  try {
    const resolved = await workspace.resolveWorkspace();
    if (resolved) {
      return ownerRouteAllowed(resolved, state.url) ? true : router.createUrlTree(['/dashboard'], { queryParams: { orgId: resolved.orgId, accessDenied: true } });
    }
  } catch (error) {
    console.warn('[OwnerPortalGuard] owner workspace lookup failed:', error);
  }

  if (!isOwnerPortalSession(session)) {
    window.location.href = wrongPortalRedirectUrl(session) || `${environment.backofficeAppUrl}/dashboard`;
    return false;
  }

  const wrongPortalUrl = wrongPortalRedirectUrl(session);
  if (wrongPortalUrl) {
    window.location.href = wrongPortalUrl;
    return false;
  }

  window.location.href = `${environment.backofficeAppUrl}/dashboard`;
  return false;
};

function wrongPortalRedirectUrl(session: {
  accountType?: string | null;
  roles?: string[] | null;
}): string | null {
  const roles = session.roles ?? [];
  const accountType = String(session.accountType ?? '').toUpperCase();

  if (roles.includes('ROLE_ADMIN') || roles.includes('ROLE_SUPER_ADMIN')) {
    return `${environment.platformAdminAppUrl}/login`;
  }

  if (roles.includes('ROLE_PROPERTY_MANAGER') || accountType === 'PROPERTY_MANAGER') {
    return `${environment.backofficeAppUrl}/dashboard`;
  }

  if (roles.includes('ROLE_MAINTENANCE')) {
    return `${environment.maintenanceAppUrl}/dashboard`;
  }

  if (roles.includes('ROLE_TENANT') || accountType === 'TENANT') {
    return `${environment.tenantAppUrl}/dashboard`;
  }

  return null;
}

function isOwnerPortalSession(session: {
  accountType?: string | null;
  roles?: string[] | null;
}): boolean {
  const roles = session.roles ?? [];
  const accountType = String(session.accountType ?? '').toUpperCase();
  return roles.includes('ROLE_OWNER')
    || roles.includes('ROLE_LANDLORD')
    || accountType === 'OWNER'
    || accountType === 'LANDLORD';
}

function redirectToOwnerLogin(returnUrl: string): void {
  const loginUrl = new URL('/login', environment.ownerAppUrl);
  loginUrl.searchParams.set('returnUrl', safeOwnerReturnPath(returnUrl));
  window.location.href = loginUrl.toString();
}

async function resolveOwnerSession(api: OwnerPortalService): Promise<{
  authenticated?: boolean | null;
  accountType?: string | null;
  roles?: string[] | null;
} | null> {
  const session = await firstValueFrom(api.verifySession().pipe(timeout(8000))).catch(() => null);
  if (session?.authenticated) {
    return session;
  }

  await firstValueFrom(api.refreshSession().pipe(timeout(8000))).catch(() => null);
  await wait(300);

  return firstValueFrom(api.verifySession().pipe(timeout(8000))).catch(() => null);
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function safeOwnerReturnPath(value: string | null | undefined): string {
  if (!value || value.startsWith('http://') || value.startsWith('https://') || value.startsWith('//')) {
    return '/dashboard';
  }

  const path = value.startsWith('/') ? value : `/${value}`;
  if (path === '/login' || path.startsWith('/login?') || path.startsWith('/login/')) {
    return '/dashboard';
  }

  return path;
}
